/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { ButtonItem } from '@atlaskit/menu';
import { Divider, MenuList, MenuSection, MenuSectionHeading } from '@atlaskit/navigation-system';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import ItemWrapper from '../MenuItem/item-wrapper';

const styles = cssMap({
	menuWrap: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},

	textStyle: {
		fontWeight: token('font.weight.medium'),
		fontFamily: token('font.family.body'),
		color: token('color.text.subtle'),
	},
});

const restProps = {
	className: 'custom-item-style',
};

const messages = defineMessages({
	showMore: {
		id: 'platform.navigation-experiences.universal-create.show-more',
		defaultMessage: 'More',
	},
	showLess: {
		id: 'platform.navigation-experiences.universal-create.show-less',
		defaultMessage: 'Less',
	},
	remoteSectionHeading: {
		id: 'platform.navigation-experiences.universal-create.remote-section-heading',
		defaultMessage: 'Suggestions',
	},
});

export const MIN_ITEMS = 4;

export type MenuSectionProps = {
	children: React.ReactNode;
	compact?: boolean;
	foldable?: boolean;
	heading?: string;
	isNative?: boolean;
	itemLength: number;
	sectionIndex: number;
	showAll: boolean;
	toggleShowMore: () => void;
};

export default function MenuSectionPrimitive({
	heading,
	sectionIndex,
	itemLength,
	foldable,
	compact = false,
	isNative = true,
	toggleShowMore,
	children,
	showAll,
}: MenuSectionProps) {
	const { formatMessage } = useIntl();

	const text = showAll ? formatMessage(messages.showLess) : formatMessage(messages.showMore);

	if (!itemLength) {
		return null;
	}

	// when compact is true, it should hide both heading and divider
	// when compact is false or the section is native, it should only show the divider if it is not the first section
	const shouldShowDivider = (isNative || !compact) && sectionIndex !== 0;
	const shouldShowHeading = !!(heading && (isNative || !compact));

	return (
		<MenuSection key={`menu-section-${sectionIndex}`}>
			{shouldShowDivider && <Divider />}
			{shouldShowHeading && (
				<ItemWrapper>
					<MenuSectionHeading>{heading}</MenuSectionHeading>
				</ItemWrapper>
			)}
			{fg('platform_navx_universal_create_a11y_fix') ? (
				/* A11y: Use MenuList to create proper semantic structure */
				<MenuList>{children}</MenuList>
			) : (
				children
			)}
			{foldable && itemLength > MIN_ITEMS && (
				<ItemWrapper>
					<ButtonItem
						{...restProps}
						iconBefore={<ShowMoreHorizontalIcon label="" color="var(--ds-text-subtle)" />}
						onClick={toggleShowMore}
						testId="toggle-more-less-button"
						aria-label={`Toggle for ${text}${heading ? ` ${heading}` : ''} options`}
					>
						<Box as="span" xcss={styles.textStyle}>
							{text}
						</Box>
					</ButtonItem>
				</ItemWrapper>
			)}
		</MenuSection>
	);
}
